import { MutationCache, QueryClient } from "@tanstack/react-query";
import type {
	PersistedClient,
	Persister,
} from "@tanstack/react-query-persist-client";
import type { AxiosError } from "axios";
import { del, get, set } from "idb-keyval";
import { compress, decompress } from "lz-string";
import registerDefaultMutations from "./mutations";

declare module "@tanstack/react-query" {
	interface Register {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		defaultError: AxiosError<any, any>;
	}
}

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5,
			gcTime: 1000 * 60 * 60 * 24,
			refetchOnReconnect: true,
		},
	},
	mutationCache: new MutationCache(),
});

registerDefaultMutations(queryClient);

const encryption = {
	encrypt: (data: PersistedClient) => {
		if (import.meta.env.PROD) {
			return compress(JSON.stringify(data));
		}
		return data;
	},
	decrypt: (data: string) => {
		if (import.meta.env.PROD) {
			return JSON.parse(decompress(data));
		}
		return data;
	},
};

function createIDBPersister(idbValidKey: IDBValidKey = "rq_cache") {
	return {
		persistClient: async (client: PersistedClient) => {
			const value = encryption.encrypt(client);
			await set(idbValidKey, value);
		},
		restoreClient: async () => {
			const res = await get<string>(idbValidKey);
			if (!res) return undefined;
			const client = encryption.decrypt(res) as PersistedClient;
			return client;
		},
		removeClient: async () => {
			await del(idbValidKey);
		},
	} as Persister;
}

export const queryClientPersister = createIDBPersister();
